import { action, makeAutoObservable, observable } from "mobx";

import { RoutePath } from "../types";
import { UserStore } from "./UserStore";

export class NavigationStore {
  navigateFunc: any; // Will store the useNavigate() hook (we must set it externally from a component - App.tsx).
  @observable previousRoute: RoutePath = RoutePath.HOME;
  @observable currentRoute: RoutePath = RoutePath.HOME;

  /**
   * Login Button
   */
  @observable footerNavVisible = true;

  /**
   * Login Button
   */
  @observable loginBtnVisible = false;

  /**
   * Back Button
   */
  @observable backBtnVisible = true;

  // Override the back button handler. This will prevent actually going back.
  // Be sure to call resetBackBtnOnClick() when the override is no longer needed
  @observable backBtnOnClick?: () => void;
  // Additional handler for when we are navigating back
  // Useful for clearing any mobx variables out
  @observable onNavigateBack?: () => void;

  /**
   * Restart Button
   */
  @observable restartBtnVisible = true;

  /**
   * Subtotal section
   */
  @observable subtotalVisible = false;

  @observable offerTickerVisible = false;
  /**
   * Next Button vars
   */
  @observable nextBtnVisible = true;
  @observable nextBtnEnabled = true;
  // Override the next button handler
  // Be sure to call resetNextBtnOnClick() (or resetNextBtn) when the override is no longer needed
  @observable nextBtnOnClick?: () => void;
  // Override the next button handler
  // Be sure to call resetNextBtnText() (or resetNextBtn) when the override is no longer needed
  @observable nextBtnText: string | undefined = undefined;

  // We keep a reference to userStore for figuring out nav routes
  private userStore: UserStore;

  constructor(userContext: UserStore) {
    this.userStore = userContext;
    makeAutoObservable(this);
  }

  /**
   * Reset the entire store back to default state (e.g. when Restart button pressed)
   */
  @action
  reset() {
    this.footerNavVisible = true;
    this.loginBtnVisible = false;
    this.backBtnVisible = true;
    this.restartBtnVisible = true;
    this.subtotalVisible = true;
    this.offerTickerVisible = false;
    this.nextBtnVisible = true;
    this.nextBtnEnabled = true;
    this.backBtnOnClick = undefined;
    this.nextBtnOnClick = undefined;
    this.nextBtnText = undefined;
  }

  @action
  setPreviousRoute(route: RoutePath) {
    this.previousRoute = route;
  }

  @action
  setCurrentRoute(route: RoutePath) {
    this.currentRoute = route;
  }

  @action
  setFooterNavVisible(isVisible: boolean) {
    this.footerNavVisible = isVisible;
  }

  /**
   * Login Button Actions
   */
  @action
  setLoginButtonVisible(isVisible: boolean) {
    this.loginBtnVisible = isVisible;
  }

  /**
   * Back Button Actions
   */
  @action
  setBackButtonVisible(isVisible: boolean) {
    this.backBtnVisible = isVisible;
  }

  @action
  setBackBtnOnClick(fn: () => void) {
    this.backBtnOnClick = fn;
  }
  @action
  resetBackBtnOnClick() {
    this.backBtnOnClick = undefined;
  }

  /*
   * Additional handler for when navigation goes back
   * Be sure to call resetOnNavigateBack() when no longer needed (e.g. on component unmount)
   */
  @action
  setOnNavigateBack(fn: () => void) {
    this.onNavigateBack = fn;
  }
  @action
  resetOnNavigateBack() {
    this.onNavigateBack = undefined;
  }

  /**
   * Restart Button Actions
   */
  @action
  setRestartButtonVisible(isVisible: boolean) {
    this.restartBtnVisible = isVisible;
  }

  /**
   * Subtotal Actions
   */
  @action
  setSubtotalVisible(isVisible: boolean) {
    this.subtotalVisible = isVisible;
  }

  @action
  setOfferTickerVisible(isVisible: boolean) {
    this.offerTickerVisible = isVisible;
  }

  /**
   * Next Button Actions
   */
  @action
  setNextButtonVisible(isVisible: boolean) {
    this.nextBtnVisible = isVisible;
  }

  @action
  setNextButtonEnabled(isEnabled: boolean) {
    this.nextBtnEnabled = isEnabled;
  }

  /*
   * Override the Next Button handler.
   * Be sure to call resetNextBtnOnClick() (or resetNextBtn) when the override is no longer needed
   */
  @action
  setNextBtnOnClick(fn: () => void) {
    this.nextBtnOnClick = fn;
  }
  @action
  resetNextBtnOnClick() {
    this.nextBtnOnClick = undefined;
  }

  /*
   * Override the Next Button Text
   * Be sure to call resetNextBtnText() (or resetNextBtn) when the override is no longer needed
   */
  setNextBtnText(text: string | undefined) {
    this.nextBtnText = text;
  }
  @action
  resetNextBtnText() {
    this.nextBtnText = undefined;
  }

  /**
   * Completely reset the next button (remove custom handlers, text, etc all in one go)
   */
  @action
  resetNextBtn() {
    this.resetNextBtnOnClick();
    this.resetNextBtnText();
  }

  @action setFooterModePrimary() {
    this.setFooterNavVisible(true);
    this.resetNextBtn();
    this.setNextButtonVisible(true);
    this.setRestartButtonVisible(true);
    this.setBackButtonVisible(true);
    this.setSubtotalVisible(true);
    this.setLoginButtonVisible(false);
  }

  setNavigationFunc(func: any) {
    this.navigateFunc = func;
  }

  goTo(route: RoutePath, state?: any) {
    if (this.navigateFunc) {
      if(route === RoutePath.HOME){
        route = RoutePath.CHOOSE_FLOOR_PLAN;
      }
      this.navigateFunc(route, {
        replace: true,
        state,
      });
    }
  }

  @action
  goNext() {
    this.goTo(this.getNextRoute(this.currentRoute), { direction: "push" });
  }

  @action
  goBack() {
    this.goTo(this.getPreviousRoute(this.currentRoute), {
      direction: "pop",
    });
  }

  getNextRoute(currentRoute: RoutePath): RoutePath {
    if (currentRoute === RoutePath.LOGIN) {
      // Login is in intermittent screen, and should be skipped in browser history
      return this.getNextRoute(this.previousRoute);
    }
    const routeList = this.userStore.getRouteList();
    const currentRouteIndex = routeList.indexOf(currentRoute);
    const nextRouteIndex = currentRouteIndex + 1;
    let nextRoute: RoutePath = currentRoute;
    if (routeList.length > nextRouteIndex) {
      nextRoute = routeList[nextRouteIndex];

      if (
        nextRoute === RoutePath.PROMOTIONS &&
        this.userStore.promotions.length === 0
      ) {
        // Skip past promotions
        nextRoute = this.getNextRoute(nextRoute);
      } else if (
        nextRoute === RoutePath.SPECIAL_OFFERS &&
        this.userStore.specialOffers.length === 0
      ) {
        nextRoute = this.getNextRoute(nextRoute);
      } else if (nextRoute === RoutePath.SIGN_UP && this.userStore.isLoggedIn) {
        nextRoute = this.getNextRoute(nextRoute);
      }
    }
    return nextRoute;
  }

  getPreviousRoute(currentRoute: RoutePath): RoutePath {
    if (currentRoute === RoutePath.LOGIN) {
      // Login is in intermittent screen, and should be skipped in browser history
      return this.userStore.previousRoute;
    }

    const routeList = this.userStore.getRouteList();
    const currentRouteIndex = routeList.indexOf(currentRoute);
    const nextRouteIndex = currentRouteIndex - 1;
    let nextRoute: RoutePath = currentRoute;
    if (routeList.length > nextRouteIndex) {
      nextRoute = routeList[nextRouteIndex];

      if (
        nextRoute === RoutePath.PROMOTIONS &&
        this.userStore.promotions.length === 0
      ) {
        // Skip past promotions
        nextRoute = this.getPreviousRoute(nextRoute);
      } else if (
        nextRoute === RoutePath.SPECIAL_OFFERS &&
        this.userStore.specialOffers.length === 0
      ) {
        nextRoute = this.getPreviousRoute(nextRoute);
      } else if (nextRoute === RoutePath.LOGIN) {
        nextRoute = this.getPreviousRoute(nextRoute);
      } else if (nextRoute === RoutePath.SIGN_UP) {
        nextRoute = this.getPreviousRoute(nextRoute);
      }
    }
    return nextRoute;
  }
}
